import { Icon24Spinner } from '@vkontakte/icons';
import classNames from 'clsx';
import { xray } from 'lib/xray';
import React, { FC, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IS_CHOSEN_PUBLIC_FILE } from 'reactApp/appHelpers/configHelpers';
import { useNormalizedOvidius } from 'reactApp/hooks/useNormalizedOvidius';
import { CloudFile, EStorageType } from 'reactApp/modules/storage/storage.types';
import { PdfViewer } from 'reactApp/ui/PdfViewer/PdfViewer';
import { Spinner } from 'reactApp/ui/Spinner/Spinner';
import { getAttachType } from 'reactApp/utils/downloadDocumentHelpers';
import { sendLoadTime } from 'reactApp/utils/sendLoadTime';

import styles from './NormalizedConvertedViewerPdf.css';

const onError = (error: any): void => {
    xray.send('norm_pdf_view_err', {
        rlog: 'cloud_norm_pdf_err',
        rlog_message: { error },
    });
};

interface Props {
    storage?: EStorageType | null;
    file: CloudFile;
    scale: number;
    setContentForPrint: (content: ArrayBuffer[] | null) => void;
    renderPdfFallback: () => ReactElement | null;
}

// eslint-disable-next-line max-lines-per-function
export const NormalizedConvertedViewerPdf: FC<Props> = ({ storage, file, scale, setContentForPrint, renderPdfFallback }) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const [isLoadSuccess, setIsLoadSuccess] = useState(false);
    const mountTime = useRef<number>(0);
    const attachType = useMemo(() => getAttachType(file), [file]);

    const { content, isLoad, partsAmount, isError } = useNormalizedOvidius({
        url: file.id,
        setContentForPrint,
        storage,
        file,
        attachType,
    });

    useEffect(() => {
        xray.send(`norm_pdf${attachType ? `_${attachType}` : ''}_view_open`, { i: { [file.ext]: 1 } });
        mountTime.current = Date.now();
    }, []);

    useEffect(() => {
        if (content.length !== 0) {
            xray.send(`norm_ovidius${attachType ? `_${attachType}` : ''}_content_success`, { i: { [file.ext]: 1 } });
        }
    }, [content]);

    const onLoadSuccessCallback = useCallback(() => {
        if (!isLoadSuccess) {
            sendLoadTime(mountTime.current, `norm_ovidius${attachType ? `_${attachType}` : ''}_load_time`);
        }
        xray.send(`norm_ovidius${attachType ? `_${attachType}` : ''}_load_success`, { i: { [file.ext]: 1 } });
        setIsLoadSuccess(true);
    }, [isLoadSuccess, attachType]);

    const contentOffsets = useMemo(() => [10, 64], []);

    if (isError) {
        return renderPdfFallback();
    }

    if (!isLoad) {
        return <Spinner />;
    }

    const loadingPagesCondition = partsAmount !== 0 && partsAmount > content.length && isLoadSuccess;

    return (
        <div className={classNames(styles.container, { [styles.singlePublic]: IS_CHOSEN_PUBLIC_FILE })}>
            <div ref={contentRef} className={styles.content}>
                <div>
                    {!!content.length &&
                        content.map((partContent, index) => (
                            <PdfViewer
                                key={index}
                                documentScale={scale}
                                content={partContent}
                                onError={onError}
                                renderTextLayer
                                onLoadSuccessCallback={onLoadSuccessCallback}
                                scrollWrapperRef={contentRef}
                                contentOffsets={contentOffsets}
                            />
                        ))}
                </div>

                {loadingPagesCondition && (
                    <div className={styles.loadingBlock}>
                        <Icon24Spinner className={styles.spinnerIcon} height={30} />
                        <p className={styles.loadingBlockText}>Загрузка страниц</p>
                    </div>
                )}
            </div>
        </div>
    );
};

NormalizedConvertedViewerPdf.displayName = 'NormalizedConvertedViewerPdf';
